import { useState, useEffect, useCallback, createContext, useContext, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import HomePage from "@/pages/HomePage";
import ContentPage from "@/pages/ContentPage";
import StorePage from "@/pages/StorePage";
import ContactPage from "@/pages/ContactPage";
import TeamPage from "@/pages/TeamPage";
import CommitteesPage from "@/pages/CommitteesPage";
import CommitteeDetailPage from "@/pages/CommitteeDetailPage";
import LocalOrgsPage from "@/pages/LocalOrgsPage";
import LocalOrgDetailPage from "@/pages/LocalOrgDetailPage";
import NewsPage from "@/pages/NewsPage";
import NewsDetailPage from "@/pages/NewsDetailPage";
import PartnersPage from "@/pages/PartnersPage";
import MediaGalleryPage from "@/pages/MediaGalleryPage";
import ContentHubPage from "@/pages/ContentHubPage";
import ContentHubDetailPage from "@/pages/ContentHubDetailPage";
import AppRedirectPage from "@/pages/AppRedirectPage";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import { Toaster } from "@/components/ui/sonner";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

function App() {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("admin_token");
    if (token) {
      try {
        const res = await api.get("/auth/me");
        setAdmin(res.data);
      } catch {
        localStorage.removeItem("admin_token");
        setAdmin(null);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    // Seed data on first load
    api.post("/seed").catch(() => {});
  }, []);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    localStorage.setItem("admin_token", res.data.token);
    setAdmin(res.data.admin);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    setAdmin(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-jci-blue border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ admin, login, logout }}>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          {/* Admin routes - no header/footer */}
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={admin ? <AdminDashboard /> : <Navigate to="/admin" />} />
          {/* Public routes */}
          <Route path="/*" element={<PublicLayout />} />
        </Routes>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

function PublicLayout() {
  return (
    <>
      <Header />
      <main className="min-h-screen">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/team/:category" element={<TeamPage />} />
          <Route path="/committees" element={<CommitteesPage />} />
          <Route path="/committee/:slug" element={<CommitteeDetailPage />} />
          <Route path="/local-organizations" element={<LocalOrgsPage />} />
          <Route path="/local-org/:slug" element={<LocalOrgDetailPage />} />
          <Route path="/news-press" element={<NewsPage />} />
          <Route path="/news/:id" element={<NewsDetailPage />} />
          <Route path="/partners" element={<PartnersPage />} />
          <Route path="/media-gallery" element={<MediaGalleryPage />} />
          <Route path="/content-hub" element={<ContentHubPage />} />
          <Route path="/content-hub/:slug" element={<ContentHubDetailPage />} />
          <Route path="/page/:slug" element={<ContentPage />} />
          <Route path="/store" element={<StorePage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/download" element={<AppRedirectPage />} />
        </Routes>
      </main>
      <Footer />
    </>
  );
}

export default App;
